import React, { useState } from 'react'
import { Row, EditWrapper, Column, Label, State, ClickableIcon, CompleteProfileMessage } from './styled'
import { SuccessMessage, CodeInput, Button, IconButton, Input, CompleteProfileInfo } from '@sabavision/dio'
import { useTranslation } from 'react-i18next'
import { createOTP, getTokenRequest, changeIdentityAttribute } from '../../../api/profile'
import { Istate } from '../../../reducers/types'
import { IReason } from '../../../api/types'
import { useDispatch, useSelector } from 'react-redux'
import Validate from '../../../validations'
import validator from '../../../validations/schema/auth'
import { updateToastData } from '../../../actions/toast'

interface Idata {
  otp: string
  password: string
  retypePassword: string
  step: 'getOTP' | 'getToken' | 'changePassword'
  token: string
}
interface IgetTokenData {
  email?: string
  mobile?: string
  otp: string
  reason: IReason
}

interface IChangePasswordData {
  attribute: 'email' | 'password' | 'mobile'
  value: string
  existing_attribute_token?: string
  new_attribute_token: string
}

function SecuritySetting() {
  const { t } = useTranslation('translation', { keyPrefix: 'sidebar' })
  const user = useSelector((state: Istate) => state.user)

  const dispatch = useDispatch()

  const [edit, setEdit] = useState<boolean>(false)
  const [data, setData] = useState<Idata>({
    otp: '',
    password: '',
    retypePassword: '',
    step: 'getOTP',
    token: ''
  })
  const [disabledOtp, setDisabledOtp] = useState<boolean>(true)

  const updateData = (key: string, value: string) => {
    setData({ ...data, [key]: value })
  }

  const getOtpRequest = () => {
    const requestData = {
      mobile: user.mobile,
      reason: 'update_password'
    }
    createOTP(
      requestData,
      () => {
        setDisabledOtp(false)
        setData({ ...data, step: 'getToken' })
        dispatch(
          updateToastData({
            message: 'کد با موفقیت ارسال شد.',
            type: 'success'
          })
        )
      },
      (error: any) => {
        dispatch(
          updateToastData({
            message: error.response?.data?.message,
            type: 'error'
          })
        )
        console.log(error)
      }
    )
  }

  const getToken = () => {
    const requestData: IgetTokenData = {
      reason: 'update_password',
      otp: data.otp
    }
    if (user.mobile) {
      requestData.mobile = user.mobile
    } else if (user.email) {
      requestData.email = user.mobile
    }
    getTokenRequest(
      requestData,
      (token: any) => {
        setData({ ...data, step: 'changePassword', token })
      },
      (error: any) => {
        console.log(error)
      }
    )
  }

  const changePasswordRequest = () => {
    const requestData: IChangePasswordData = {
      attribute: 'password',
      value: data.password,
      new_attribute_token: data.token
    }

    changeIdentityAttribute(
      user.unique_id,
      requestData,
      () => {
        setEdit(false)
        dispatch(
          updateToastData({
            message: 'رمز با موفقیت بروزرسانی شد.',
            type: 'success'
          })
        )
      },
      (error: any) => {
        console.log(error)
      }
    )
  }

  const [error, setErrors] = useState<Record<string, string>>({})
  const validationData = (data: Record<string, string>) => {
    let valid = true
    const constrains = validator()
    let err = {}
    Object.keys(data).forEach((field) => {
      const errorMessage = Validate(data, field, constrains)
      err = { ...err, [field]: errorMessage }
      if (errorMessage) {
        valid = false
      }
    })
    setErrors({ ...err })
    return valid
  }

  const handleRequest = () => {
    let valid: boolean
    if (data.step === 'getOTP') {
      getOtpRequest()
    } else if (data.step === 'getToken') {
      valid = validationData({ otp: data.otp })
      if (valid) {
        getToken()
      }
    } else {
      valid = validationData({ password: data.password, retypePassword: data.retypePassword })
      if (valid) {
        changePasswordRequest()
      }
    }
  }

  const returnFields = () => {
    if (data.step === 'changePassword') {
      return (
        <>
          <Input
            label={t('password')}
            handleChange={(value) => {
              updateData('password', value)
            }}
            type="password"
            name="password"
            showErrorMessage={true}
            error={error.password ? t(error.password) : null}
          />
          <Input
            label={t('confirm_password')}
            handleChange={(value) => {
              updateData('retypePassword', value)
            }}
            type="password"
            name="retypePassword"
            error={error.retypePassword ? t(error.retypePassword) : null}
          />
        </>
      )
    } else {
      return (
        <>
          <SuccessMessage message={t('security_setting_help', { mobile: user.mobile ? user.mobile : user.email })} />
          <div className="otp-container">
            <CodeInput
              handleChange={(value) => {
                updateData('otp', value)
              }}
              label={t('fill_otp')}
              disabled={disabledOtp}
              value={data.otp}
              error={error.otp && t(error.otp)}
            />
          </div>
        </>
      )
    }
  }

  if (edit) {
    return (
      <EditWrapper>
        {returnFields()}
        <Row gap="4px" className="actions">
          <Button
            data-test="button-security-settings"
            label={t(data.step === 'getOTP' ? 'confirm_and_get_otp' : 'confirm')}
            handleClick={() => {
              handleRequest()
            }}
            fullWidth
            fontSize="12px"
            radius="10px"
          />
          <IconButton
            icon="iris-x"
            handleClick={() => {
              setEdit(false)
            }}
          />
        </Row>
      </EditWrapper>
    )
  }

  return (
    <Column>
      <Row className="space-between">
        <span>
          <Label className="label">{t('password')}:</Label>
          {user.password_set ? <State>* * * * * *</State> : <State>{t('undefined')}</State>}
        </span>
        {(user.mobile || user.email) && (
          <ClickableIcon
            className="iconsax-edit-2"
            onClick={() => {
              setEdit(true)
            }}
          />
        )}
      </Row>
      {!user.mobile && !user.email && (
        <CompleteProfileInfo message="" disabled={false}>
          <CompleteProfileMessage>
            {t('complete_profile_message_1')} &nbsp;
            <span className="link">{t('complete_profile_message_link')}</span>
            &nbsp; {t('complete_profile_message_2')}
          </CompleteProfileMessage>
        </CompleteProfileInfo>
      )}
    </Column>
  )
}

export default SecuritySetting
